import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button as MuiButton, Typography } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { VendorsListingService, handleApiError } from '../../services';
import { Vendor } from '../../types';
import { Tabs, Dropdown, Modal, type Tab, type DropdownOption, ImageCard } from '../../components/Common';
import { PageHeader } from '../../components/Layout';
import { toast } from 'react-toastify';

// Product interface
export interface Product {
  id: string;
  title: string;
  price: string;
  description: string;
  image: string;
  status: 'active' | 'pending' | 'suspended' | 'deactivated';
  category: string;
}

const VendorProducts: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  
  // Determine active tab based on current route
  const getActiveTabFromRoute = useCallback((): 'profile' | 'products' | 'orders' | 'transactions' => {
    const path = location.pathname;
    if (path.includes('/transactions')) return 'transactions';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/products')) return 'products';
    if (path.includes('/vendors/') && !path.includes('/orders') && !path.includes('/products') && !path.includes('/transactions')) return 'profile';
    return 'products'; // default
  }, [location.pathname]);
  
  const [activeTab, setActiveTab] = useState<'profile' | 'products' | 'orders' | 'transactions'>(() => {
    const path = location.pathname;
    if (path.includes('/transactions')) return 'transactions';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/products')) return 'products';
    if (path.includes('/vendors/') && !path.includes('/orders') && !path.includes('/products') && !path.includes('/transactions')) return 'profile';
    return 'products'; // default
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>('Any status');
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productDetailsModalOpen, setProductDetailsModalOpen] = useState(false);
  const [productsByCategory, setProductsByCategory] = useState<Array<{ id: number | string; category: string; products: Product[] }>>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditVendor = () => {
    // TODO: Implement edit vendor
    handleMenuClose();
  };

  const handleViewProfile = () => {
    navigate(`/vendors/${id}`);
    handleMenuClose();
  };

  const statusOptions: DropdownOption[] = [
    { label: 'Any status', value: 'Any status', onClick: () => { setStatusFilter('Any status'); setStatusFilterAnchor(null); } },
    { label: 'Active', value: 'Active', onClick: () => { setStatusFilter('Active'); setStatusFilterAnchor(null); } },
    { label: 'Pending review', value: 'Pending review', onClick: () => { setStatusFilter('Pending review'); setStatusFilterAnchor(null); } },
    { label: 'Suspended', value: 'Suspended', onClick: () => { setStatusFilter('Suspended'); setStatusFilterAnchor(null); } },
    { label: 'Deactivated', value: 'Deactivated', onClick: () => { setStatusFilter('Deactivated'); setStatusFilterAnchor(null); } },
  ];

  const menuOptions: DropdownOption[] = [
    { label: 'Edit Vendor', value: 'edit', onClick: handleEditVendor },
    { label: 'View Profile', value: 'profile', onClick: handleViewProfile },
  ];

  const tabs: Tab[] = [
    { key: 'profile', label: 'Profile' },
    { key: 'products', label: 'Products' },
    { key: 'orders', label: 'Orders' },
    { key: 'transactions', label: 'Transactions' },
  ];

  const handleTabChange = (tabKey: string) => {
    if (tabKey === 'profile') navigate(`/vendors/${id}`);
    else if (tabKey === 'products') navigate(`/vendors/${id}/products`);
    else if (tabKey === 'orders') navigate(`/vendors/${id}/orders`);
    else if (tabKey === 'transactions') navigate(`/vendors/${id}/transactions`);
  };

  const fetchVendor = useCallback(async () => {
    if (!id) return;
    
    try {
      const vendorData = await VendorsListingService.getVendorById(id);
      setVendor(vendorData);
    } catch (error: any) {
      console.error('Error fetching vendor:', error);
    }
  }, [id]);

  const fetchProducts = useCallback(async () => {
    if (!id) return;

    try {
      setProductsLoading(true);
      setProductsError(null);
      
      const productsData = await VendorsListingService.getVendorProducts(id);
      
      console.log('📦 Fetched products data:', productsData);
      
      // Handle different response structures
      // If it's already grouped by category
      if (Array.isArray(productsData) && productsData.length > 0 && productsData[0].category) {
        setProductsByCategory(productsData.map((cat: any, index: number) => ({
          id: cat.id || cat.categoryId || index,
          category: cat.category || cat.name || 'Uncategorized',
          products: (cat.products || []).map((p: any) => ({
            id: p.id || p.productId || '',
            title: p.title || p.name || '',
            price: p.price || '$0.00',
            description: p.description || '',
            image: p.image || p.imageUrl || '',
            status: (p.status || 'active').toLowerCase() as Product['status'],
            category: cat.category || cat.name || 'Uncategorized',
          })),
        })));
      } 
      // If it's a flat array of products, group by category
      else if (Array.isArray(productsData)) {
        const grouped = productsData.reduce((acc: any, product: any) => {
          const category = product.category || 'Uncategorized';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push({
            id: product.id || product.productId || '',
            title: product.title || product.name || '',
            price: product.price || '$0.00',
            description: product.description || '',
            image: product.image || product.imageUrl || '',
            status: (product.status || 'active').toLowerCase() as Product['status'],
            category: category,
          });
          return acc;
        }, {});

        setProductsByCategory(
          Object.entries(grouped).map(([category, products], index) => ({
            id: index,
            category,
            products: products as Product[],
          }))
        );
      }
      // If it's an object with products array
      else if (productsData.products && Array.isArray(productsData.products)) {
        const grouped = productsData.products.reduce((acc: any, product: any) => {
          const category = product.category || 'Uncategorized';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push({
            id: product.id || product.productId || '',
            title: product.title || product.name || '',
            price: product.price || '$0.00',
            description: product.description || '',
            image: product.image || product.imageUrl || '',
            status: (product.status || 'active').toLowerCase() as Product['status'],
            category: category,
          });
          return acc;
        }, {});

        setProductsByCategory(
          Object.entries(grouped).map(([category, products], index) => ({
            id: index,
            category,
            products: products as Product[],
          }))
        );
      } else {
        // Fallback: empty array
        setProductsByCategory([]);
      }
    } catch (err: any) {
      console.error('Error fetching products:', err);
      const errorMessage = handleApiError(err);
      setProductsError(errorMessage);
      toast.error('Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  }, [id]);

  // Filter products by status
  const getFilteredProductsByCategory = () => {
    if (statusFilter === 'Any status') {
      return productsByCategory;
    }

    const statusMap: Record<string, string> = {
      'Active': 'active',
      'Pending review': 'pending',
      'Suspended': 'suspended',
      'Deactivated': 'deactivated',
    };

    const targetStatus = statusMap[statusFilter];
    if (!targetStatus) return productsByCategory;

    return productsByCategory.map(category => ({
      ...category,
      products: category.products.filter((p: Product) => p.status === targetStatus),
    })).filter(category => category.products.length > 0);
  };

  // Sync activeTab with route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [getActiveTabFromRoute]);

  useEffect(() => {
    fetchVendor();
  }, [fetchVendor]);

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
    }
  }, [activeTab, fetchProducts]);

  const handleProductActionClick = (productId: string, event: React.MouseEvent) => {
    // Find product by iterating through categories
    for (const category of productsByCategory) {
      const product = category.products.find((p: Product) => p.id === productId);
      if (product) {
        setSelectedProduct(product);
        setProductDetailsModalOpen(true);
        return;
      }
    }
  };

  const handleCloseProductDetails = () => {
    setProductDetailsModalOpen(false);
    setSelectedProduct(null);
  };

  const filteredCategories = getFilteredProductsByCategory();

  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: "24px 16px",
          sm: "40px 40px",
          md: "40px 80px",
        },
        gap: "24px",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Page Header */}
      <PageHeader
        title="Products"
        showIconButton={true}
        onIconButtonClick={handleMenuOpen}
      />

      {/* Tabs and Filter Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          gap: "32px",
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Tabs and Filter */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {/* Status Filter - Only show on products tab */}
          {activeTab === "products" && (
            <Box>
              <MuiButton
                onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
                sx={{
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                  color: 'var(--palette-light-text-primary-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  height: '39px',
                  padding: '10px 20px 7px',
                  borderRadius: '16px',
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  maxWidth: '200px',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                    opacity: 0.9,
                  },
                  '& .MuiButton-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  },
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                  {statusFilter}
                </span>
                <KeyboardArrowDownIcon sx={{ fontSize: '20px', flexShrink: 0 }} />
              </MuiButton>
              <Dropdown
                anchorEl={statusFilterAnchor}
                open={Boolean(statusFilterAnchor)}
                onClose={() => setStatusFilterAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                options={statusOptions}
              />
            </Box>
          )}
        </Box>

        {/* Products Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'auto',
            gap: '16px',
          }}
        >
          {activeTab === "products" && productsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography>Loading products...</Typography>
            </Box>
          )}
          {activeTab === "products" && !productsLoading && productsError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography color="error">{productsError}</Typography>
            </Box>
          )}
          {activeTab === "products" && !productsLoading && !productsError && filteredCategories.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography>No products found</Typography>
            </Box>
          )}
          {activeTab === "products" && !productsLoading && !productsError && filteredCategories.map((category) => (
            <Box key={category.id} sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              {/* Category Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography
                  sx={{
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: '1.2',
                    letterSpacing: '-0.5%',
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  {category.category}
                </Typography>
              </Box>

              {/* Product Cards Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(4, 1fr)',
                  },
                  gap: '10px',
                  width: '100%',
                }}
              >
                {category.products.map((product: Product) => (
                  <ImageCard
                    key={product.id}
                    id={product.id}
                    image={product.image}
                    title={product.title}
                    subtitle={product.price}
                    description={product.description}
                    status={product.status}
                    onActionClick={handleProductActionClick}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Action Menu Dropdown */}
      <Dropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        options={menuOptions}
      />

      {/* Product Details Modal */}
      <Modal
        open={productDetailsModalOpen}
        onClose={handleCloseProductDetails}
        title={selectedProduct ? selectedProduct.title : 'Product Details'}
        width={800}
      >
        {selectedProduct && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {selectedProduct.image && (
              <Box
                component="img"
                src={selectedProduct.image}
                alt={selectedProduct.title}
                sx={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  borderRadius: '16px',
                }}
              />
            )}
            <Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: '8px' }}>
                Price
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {selectedProduct.price}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: '8px' }}>
                Description
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {selectedProduct.description}
              </Typography>
            </Box>
            {selectedProduct.category && (
              <Box>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: '8px' }}>
                  Category
                </Typography>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {selectedProduct.category}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default VendorProducts;

